import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Loader2, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState("loading");
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.get(`${API}/payments/stripe/status/${sessionId}`);
        setPaymentInfo(response.data);
        
        if (response.data.payment_status === "paid") {
          setStatus("success");
          
          // Mark discount code as used if there was one
          const pendingDiscountCode = localStorage.getItem('pending_discount_code');
          if (pendingDiscountCode) {
            try {
              await axios.post(`${API}/discounts/apply/${pendingDiscountCode}`);
            } catch (e) {
              console.error("Failed to mark discount as used:", e);
            }
            localStorage.removeItem('pending_discount_code');
          }
          
          // Clear localStorage after successful payment
          localStorage.removeItem('shipping_estimate');
          localStorage.removeItem('order_instructions');
          localStorage.removeItem('applied_discount');
        } else {
          setStatus("pending");
        }
      } catch (error) {
        console.error("Error verifying payment:", error);
        setStatus("error");
      }
    };

    verifyPayment();
  }, [sessionId]);

  const handleDownloadInvoice = async () => {
    if (!sessionId || !paymentInfo?.order_id) {
      toast.error("Unable to generate invoice. Please contact support.");
      return;
    }

    setDownloadingInvoice(true);
    try {
      const response = await axios.get(
        `${API}/orders/invoice/${paymentInfo.order_id}`,
        { responseType: 'blob' }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice-${paymentInfo.order_id}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("Invoice downloaded successfully!");
    } catch (error) {
      console.error("Error downloading invoice:", error);
      toast.error("Failed to download invoice. Please try again later.");
    } finally {
      setDownloadingInvoice(false);
    }
  };

  if (status === "loading") {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center py-16">
          <Loader2 className="h-16 w-16 mx-auto mb-6 animate-spin text-accent" />
          <h1 className="text-2xl font-heading font-bold mb-4">Verifying Payment...</h1>
          <p className="text-muted-foreground">Please wait while we confirm your order.</p>
        </div>
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-4 md:px-8 text-center py-16">
          <h1 className="text-2xl font-heading font-bold mb-4">Something went wrong</h1>
          <p className="text-muted-foreground mb-8">
            We couldn't verify your payment. Please contact support if you were charged.
          </p>
          <Link to="/products">
            <Button className="rounded-full uppercase tracking-widest">
              Return to Shop
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-24 pb-16" data-testid="checkout-success">
      <div className="max-w-2xl mx-auto px-4 md:px-8 text-center py-16">
        <CheckCircle className="h-20 w-20 mx-auto mb-6 text-green-600" strokeWidth={1.5} />
        <h1 className="text-3xl md:text-4xl font-heading font-bold mb-4">Thank You!</h1>
        <p className="text-lg text-muted-foreground mb-8">
          Your order has been placed successfully. You'll receive a confirmation email shortly.
        </p>
        
        {paymentInfo && (
          <div className="bg-secondary p-6 mb-8 text-left">
            <h2 className="font-heading font-bold mb-4 uppercase tracking-wider">Order Details</h2>
            <div className="space-y-2 text-sm">
              {paymentInfo.order_id && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Order ID</span>
                  <span className="font-medium font-mono">{paymentInfo.order_id}</span>
                </div>
              )}
              <div className="flex justify-between">
                <span className="text-muted-foreground">Status</span>
                <span className="font-medium text-green-600 capitalize">{paymentInfo.payment_status}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Amount</span>
                <span className="font-medium price-font">
                  £{(paymentInfo.amount_total / 100).toFixed(2)} {paymentInfo.currency?.toUpperCase()}
                </span>
              </div>
            </div>
            
            {/* Download Invoice Button */}
            <div className="mt-6 pt-4 border-t border-border">
              <Button
                onClick={handleDownloadInvoice}
                disabled={downloadingInvoice}
                variant="outline"
                className="w-full rounded-full uppercase tracking-widest"
                data-testid="download-invoice-btn"
              >
                {downloadingInvoice ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Generating Invoice...
                  </>
                ) : (
                  <>
                    <Download className="mr-2 h-4 w-4" />
                    Download Invoice
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link to="/products">
            <Button variant="outline" className="rounded-full uppercase tracking-widest">
              Continue Shopping
            </Button>
          </Link>
          <Link to="/account">
            <Button className="rounded-full uppercase tracking-widest">
              View Orders
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
